import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bookmark,
  CheckCircle2,
  ChevronDown,
  Clock,
  Loader2,
  Settings,
  ShieldCheck,
  X,
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import { QuestionRenderer } from "src/components/assessment/QuestionRenderer";
import { ContentBlocks, hasGeoGebraBlock, splitFigureAndTextBlocks } from "src/components/assessment/ContentBlocks";
import { QuizTimer } from "src/components/assessment/QuizTimer";
import {
  attemptTokenKey,
  fetchAttemptStatus,
  saveDiagnosticResponse,
  submitDiagnostic,
  type DiagnosticQuestion,
} from "src/services/diagnosticService";
import { cn } from "src/lib/utils";

function parseTitleSegments(title: string): string[] {
  if (!title) return [];

  let mainPart = title;
  let boardPart = "";

  const dashMatch = title.match(/^(.*?)\s+[—–-]\s+(.*)$/);
  if (dashMatch) {
    mainPart = dashMatch[1].trim();
    boardPart = dashMatch[2].trim();
  }

  const gradeMatch = mainPart.match(/^(Grade\s+\w+|Kindergarten|Class\s+\w+)\s+(.+)$/i);
  if (gradeMatch) {
    const grade = gradeMatch[1].trim();
    const subject = gradeMatch[2].trim();
    return boardPart ? [grade, subject, boardPart] : [grade, subject];
  }

  if (boardPart) {
    return [mainPart, boardPart];
  }

  return [title];
}

export default function DiagnosticRunner() {
  const { attemptId = "" } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState<DiagnosticQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [revisions, setRevisions] = useState<Record<string, number>>({});
  const [index, setIndex] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const [showMatrixPopover, setShowMatrixPopover] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const saveTimers = useRef<Record<string, number>>({});
  const submittingRef = useRef(false);
  const autoSubmitted = useRef(false);
  const endsAt = useRef<number | null>(null);
  const matrixPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMatrixPopover) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (matrixPopoverRef.current && !matrixPopoverRef.current.contains(event.target as Node)) {
        setShowMatrixPopover(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowMatrixPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMatrixPopover]);

  useEffect(() => {
    if (!showReviewModal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowReviewModal(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showReviewModal]);

  const finish = useCallback(async () => {
    if (submittingRef.current) return;
    const token = sessionStorage.getItem(attemptTokenKey(attemptId));
    if (!token) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await submitDiagnostic(attemptId, token);
      navigate(`/diagnostic-assessment/${attemptId}/report?token=${encodeURIComponent(token)}`);
    } catch (err) {
      submittingRef.current = false;
      setSubmitting(false);
      setError(err instanceof Error ? err.message : "The assessment could not be submitted.");
    }
  }, [attemptId, navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem(attemptTokenKey(attemptId));
    if (!token) {
      setError("Open this quiz in the same browser tab where you started it.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchAttemptStatus(attemptId, token)
      .then((status) => {
        if (cancelled) return;
        if (status.status === "completed") {
          navigate(`/diagnostic-assessment/${attemptId}/report?token=${encodeURIComponent(token)}`, { replace: true });
          return;
        }
        setTitle(status.quiz.title);
        setQuestions(status.quiz.questions);
        setRemaining(status.remainingSeconds);
        const nextAnswers: Record<string, unknown> = {};
        const nextRevisions: Record<string, number> = {};
        for (const answer of status.answers) {
          nextAnswers[answer.questionId] = answer.selectedAnswer;
          nextRevisions[answer.questionId] = answer.revision;
        }
        setAnswers(nextAnswers);
        setRevisions(nextRevisions);
        if (status.status === "expired") setExpired(true);
        else if (status.remainingSeconds <= 0 && !autoSubmitted.current) {
          autoSubmitted.current = true;
          void finish();
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "The quiz could not be loaded.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [attemptId, finish, navigate]);

  useEffect(() => {
    if (loading || expired || submitting) return;
    if (endsAt.current == null) endsAt.current = Date.now() + remaining * 1000;
    const timer = window.setInterval(() => {
      const left = Math.max(0, Math.ceil((endsAt.current! - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) {
        window.clearInterval(timer);
        if (!autoSubmitted.current) {
          autoSubmitted.current = true;
          void finish();
        }
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [loading, expired, submitting, finish]);

  const persist = useCallback(async (questionId: string, value: unknown) => {
    const token = sessionStorage.getItem(attemptTokenKey(attemptId));
    if (!token) return;
    setSaveState("saving");
    const revision = revisions[questionId] ?? 1;
    try {
      const saved = await saveDiagnosticResponse(attemptId, token, questionId, value, revisions[questionId] ? revision : 1);
      setRevisions((current) => ({ ...current, [questionId]: saved.revision }));
      setSaveState("saved");
    } catch (err) {
      setSaveState("error");
      setError(err instanceof Error ? err.message : "The answer could not be saved.");
    }
  }, [attemptId, revisions]);

  const updateAnswer = (questionId: string, value: unknown) => {
    setAnswers((current) => ({ ...current, [questionId]: value }));
    window.clearTimeout(saveTimers.current[questionId]);
    saveTimers.current[questionId] = window.setTimeout(() => {
      void persist(questionId, value);
    }, 800);
  };

  const toggleFlag = (questionId: string) => {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  };

  const question = questions[index];

  // Auto-split figure vs text blocks for zero-scroll dual-stage layout
  const { figureBlocks, textBlocks } = useMemo(() => {
    if (!question) return { figureBlocks: null, textBlocks: { blocks: [] } };
    return splitFigureAndTextBlocks(question.content);
  }, [question]);

  const hasFigure = figureBlocks !== null;
  const isGeoGebra = useMemo(() => hasGeoGebraBlock(question?.content), [question]);
  const [showGeoGebraTools, setShowGeoGebraTools] = useState(true);
  const titleSegments = useMemo(() => parseTitleSegments(title), [title]);

  // Answered stats for telemetry and review modal
  const answeredCount = useMemo(() => {
    return Object.keys(answers).filter((k) => {
      const ans = answers[k];
      if (ans === undefined || ans === null || ans === "") return false;
      if (Array.isArray(ans) && ans.length === 0) return false;
      if (typeof ans === "object" && !Array.isArray(ans)) {
        const blanks = (ans as { blanks?: Record<string, string> }).blanks ?? ans;
        return Object.values(blanks).some((v) => typeof v === "string" && v.trim().length > 0);
      }
      return true;
    }).length;
  }, [answers]);

  const isCurrentAnswered = question
    ? answers[question.id] !== undefined &&
      answers[question.id] !== null &&
      answers[question.id] !== "" &&
      (!Array.isArray(answers[question.id]) || (answers[question.id] as unknown[]).length > 0)
    : false;

  const isCurrentFlagged = question ? flagged.has(question.id) : false;

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-[#faf9fe] font-sans antialiased">
      {/* 1. Distraction-Free Institutional Exam Header */}
      <header className="relative flex h-14 shrink-0 items-center justify-between border-b border-purple-100 bg-[#faf9fe] px-4 sm:px-6">
        {/* Left: Brand Mark, Exam Title & Stacked Attempt ID */}
        <div className="flex items-center gap-3 min-w-0 pr-4">
          <img
            src="/logo.png"
            alt="rfLearn logo"
            className="h-4 sm:h-4.5 w-auto object-contain shrink-0"
          />
          <div className="h-6 w-px bg-slate-200 shrink-0" aria-hidden="true" />
          <div className="flex flex-col justify-center min-w-0">
            <h1 className="flex items-center text-sm sm:text-base font-bold text-slate-900 tracking-tight leading-tight truncate">
              {titleSegments.length > 0 ? (
                titleSegments.map((segment, i) => (
                  <span key={i} className="flex items-center shrink-0">
                    {i > 0 && (
                      <span
                        className="mx-2 inline-block h-3.5 w-px bg-slate-300 shrink-0"
                        aria-hidden="true"
                      />
                    )}
                    <span className={cn(i === titleSegments.length - 1 && titleSegments.length > 2 && "font-semibold text-slate-700")}>
                      {segment}
                    </span>
                  </span>
                ))
              ) : (
                <span>Diagnostic Assessment</span>
              )}
            </h1>
            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500">
              <span className="font-mono text-xs">
                attempt: <span className="font-semibold text-purple-700">{attemptId.slice(0, 13)}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Center: Question Progress Pill with Matrix Dropdown (Centered in the pane) */}
        {questions.length > 0 && !loading && !submitting && !expired && (
          <div ref={matrixPopoverRef} className="absolute left-1/2 -translate-x-1/2 z-20">
            <button
              type="button"
              onClick={() => setShowMatrixPopover((prev) => !prev)}
              className="flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-2.5 py-1 text-xs font-bold text-purple-900 shadow-2xs hover:bg-purple-50 transition-colors"
              aria-expanded={showMatrixPopover}
              aria-label="Toggle Question Navigator"
            >
              <span className="h-2 w-2 rounded-full bg-purple-600" aria-hidden="true" />
              <span>
                Question {index + 1} of {questions.length}
              </span>
              <span className="hidden text-xs font-normal text-slate-500 sm:inline">
                ({answeredCount}/{questions.length} answered)
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-purple-600" aria-hidden="true" />
            </button>

            {/* Question Matrix Popover Menu */}
            {showMatrixPopover && (
              <div
                className="absolute left-1/2 top-10 z-50 w-72 -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-3 shadow-xl"
                role="dialog"
                aria-label="Question Navigator Palette"
              >
                <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-1.5 text-xs font-bold text-slate-700">
                  <span>QUESTION MATRIX</span>
                  <span className="font-mono text-purple-700">{questions.length} Questions</span>
                </div>
                <div className="mb-3 grid grid-cols-4 gap-1.5">
                  {questions.map((q, i) => {
                    const ans = answers[q.id];
                    const isAns = ans !== undefined && ans !== null && ans !== "";
                    const isAct = i === index;
                    const isFlg = flagged.has(q.id);
                    return (
                      <button
                        key={q.id}
                        type="button"
                        onClick={() => {
                          setIndex(i);
                          setShowMatrixPopover(false);
                        }}
                        className={cn(
                          "relative flex h-8 items-center justify-center rounded-lg text-xs font-bold transition-all",
                          isAct
                            ? "border-2 border-purple-600 bg-purple-50 text-purple-900 ring-2 ring-purple-200"
                            : isAns
                            ? "bg-purple-600 text-white hover:bg-purple-700"
                            : isFlg
                            ? "bg-amber-400 text-slate-900"
                            : "border border-slate-200 bg-white text-slate-700 hover:border-purple-300"
                        )}
                      >
                        {i + 1}
                        {isFlg && (
                          <span
                            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-600 ring-1 ring-white"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded bg-purple-600" /> Answered
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded bg-amber-400" /> Flagged
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded border border-slate-300 bg-white" /> Empty
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right: Autosave Status & Tabular Timer */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-slate-600" aria-live="polite">
            {saveState === "saving" && (
              <>
                <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" aria-hidden="true" />
                <span className="text-amber-800">Saving…</span>
              </>
            )}
            {saveState === "saved" && (
              <>
                <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                <span className="text-emerald-700">Synced</span>
              </>
            )}
            {saveState === "error" && (
              <>
                <span className="h-2 w-2 rounded-full bg-rose-500" aria-hidden="true" />
                <span className="text-rose-700">Not synced</span>
              </>
            )}
            {saveState === "idle" && (
              <>
                <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                <span className="text-slate-500">Auto-save on</span>
              </>
            )}
          </div>

          <QuizTimer remainingSeconds={remaining} compact />
        </div>
      </header>

      {/* 2. Main Workspace (Zero-Scroll Dual-Stage Layout) */}
      <main className="flex-1 overflow-hidden p-3 sm:p-4">
        {loading && (
          <div className="flex h-full items-center justify-center">
            <p className="flex items-center gap-2 text-sm font-medium text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin text-purple-600" aria-hidden="true" />
              Loading the diagnostic assessment…
            </p>
          </div>
        )}

        {!loading && error && !question && (
          <div className="mx-auto mt-12 max-w-lg rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800" role="alert">
            <div className="flex items-center gap-2 font-bold text-red-900 mb-1">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <span>Assessment Error</span>
            </div>
            <p>{error}</p>
          </div>
        )}

        {!loading && expired && (
          <div className="mx-auto mt-12 max-w-lg rounded-2xl border border-purple-200 bg-white p-8 text-center shadow-lg">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-purple-700 mb-4">
              <Clock className="h-6 w-6" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">This assessment has ended</h1>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed">
              The time window for this attempt has closed. Your recorded responses have been locked for evaluation.
            </p>
          </div>
        )}

        {submitting && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600 mb-4" aria-hidden="true" />
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Submitting assessment responses…
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Please wait while your evaluation report is prepared.
            </p>
          </div>
        )}

        {!loading && !submitting && question && !expired && (
          <div className="flex h-full w-full gap-3 sm:gap-4">
            {/* LEFT STAGE: Dedicated Figure / GeoGebra Embedding Viewport (Visible without scrolling) */}
            {hasFigure && (
              <section
                className="flex w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xs lg:w-[64%]"
                aria-label="Question Figure Stage"
              >
                <div className="flex shrink-0 items-center justify-between border-b border-purple-100 bg-[#faf9fe] px-3.5 py-2">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-purple-800">
                      Figure {index + 1}
                    </span>
                    <span className="text-xs font-semibold text-slate-800">Interactive Visual Stage</span>
                  </div>
                  {isGeoGebra ? (
                    <button
                      type="button"
                      onClick={() => setShowGeoGebraTools((prev) => !prev)}
                      title={showGeoGebraTools ? "Hide algebra inputs, toolbars, and drawers" : "Show algebra inputs, toolbars, and drawers"}
                      aria-pressed={showGeoGebraTools}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-all cursor-pointer",
                        showGeoGebraTools
                          ? "bg-purple-100 text-purple-800 border border-purple-300 shadow-2xs hover:bg-purple-200/70"
                          : "text-slate-600 hover:bg-slate-200/60 hover:text-slate-900 border border-slate-200 bg-white"
                      )}
                    >
                      <Settings
                        className={cn("h-3.5 w-3.5 transition-transform", showGeoGebraTools && "rotate-45 text-purple-700")}
                        aria-hidden="true"
                      />
                      <span>{showGeoGebraTools ? "Tools Open" : "Tools & Drawer"}</span>
                    </button>
                  ) : (
                    <span className="text-xs text-slate-500 font-mono">Reference Material</span>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden p-0">
                  <ContentBlocks content={figureBlocks} showTools={showGeoGebraTools} />
                </div>
              </section>
            )}

            {/* RIGHT STAGE: Question Prompt & Answer Choices (Takes 36% on split, or full centered card) */}
            <section
              className={cn(
                "flex flex-col justify-between overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xs p-4 sm:p-5 transition-all",
                hasFigure ? "w-full lg:w-[36%]" : "mx-auto w-full max-w-2xl"
              )}
              aria-label="Question and Answer Choices"
            >
              <div>
                {/* Question Type & Flag Toggle Header */}
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-purple-800">
                      {question.type === "TF"
                        ? "True / False"
                        : question.type === "MCQ-SC"
                        ? "Multiple Choice · Single Select"
                        : question.type === "MCQ-MC"
                        ? "Multiple Choice · Multi-Select"
                        : "Fill in the Blank"}
                    </span>
                    <span className="text-xs text-slate-500">1 mark</span>
                  </div>

                  {/* Bookmark / Flag for Review */}
                  <button
                    type="button"
                    onClick={() => toggleFlag(question.id)}
                    className={cn(
                      "flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors",
                      isCurrentFlagged
                        ? "border-amber-400 bg-amber-50 text-amber-900"
                        : "border-slate-200 bg-white text-slate-700 hover:border-amber-300 hover:bg-amber-50/50"
                    )}
                    aria-pressed={isCurrentFlagged}
                  >
                    <Bookmark
                      className={cn("h-3.5 w-3.5", isCurrentFlagged ? "fill-amber-500 text-amber-500" : "text-slate-400")}
                      aria-hidden="true"
                    />
                    <span>{isCurrentFlagged ? "Flagged for Review" : "Flag for Review"}</span>
                  </button>
                </div>

                <QuestionRenderer
                  type={question.type}
                  content={hasFigure ? textBlocks : question.content}
                  options={question.options ?? []}
                  answer={answers[question.id]}
                  onChange={(value) => updateAnswer(question.id, value)}
                />

                {error && (
                  <p className="mt-3 text-xs font-semibold text-red-700" role="alert">
                    {error}
                  </p>
                )}
              </div>

              {/* In-Card Utility Row */}
              <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
                <button
                  type="button"
                  onClick={() => updateAnswer(question.id, undefined)}
                  disabled={!isCurrentAnswered || submitting}
                  className="font-medium text-slate-500 hover:text-slate-900 disabled:opacity-40 transition-colors"
                >
                  Clear Choice
                </button>
                <span className="font-mono text-xs text-slate-400">
                  {isCurrentAnswered ? "Answer selected" : "Unattempted"}
                </span>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* 3. Bottom Sticky Navigation Dock with Integrated Question Palette Ribbon */}
      {!loading && !submitting && question && !expired && (
        <footer className="flex h-12 shrink-0 items-center justify-between border-t border-slate-200 bg-white px-4 sm:px-6">
          {/* Left: Previous */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIndex((current) => Math.max(0, current - 1))}
            disabled={index === 0 || submitting}
            className="flex items-center gap-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Previous</span>
          </Button>

          {/* Center: Space-Efficient Horizontal Question Navigator Ribbon */}
          <div
            className="flex max-w-[50vw] items-center gap-1.5 overflow-x-auto py-1 scrollbar-none"
            aria-label="Quick Question Jump"
          >
            {questions.map((q, i) => {
              const ans = answers[q.id];
              const isAns = ans !== undefined && ans !== null && ans !== "";
              const isAct = i === index;
              const isFlg = flagged.has(q.id);
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  disabled={submitting}
                  title={`Question ${i + 1}${isFlg ? " (Flagged)" : isAns ? " (Answered)" : ""}`}
                  className={cn(
                    "relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold transition-all",
                    isAct
                      ? "border-2 border-purple-600 bg-purple-50 text-purple-900 ring-2 ring-purple-200"
                      : isAns
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : isFlg
                      ? "bg-amber-400 text-slate-900 hover:bg-amber-500"
                      : "border border-slate-300 bg-white text-slate-700 hover:border-purple-400"
                  )}
                >
                  {i + 1}
                  {isFlg && (
                    <span
                      className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-red-600 ring-1 ring-white"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: Review Modal Trigger & Next / Submit Action */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowReviewModal(true)}
              disabled={submitting}
              className="hidden sm:inline-flex border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 text-xs font-semibold"
            >
              Review All
            </Button>

            {remaining === 0 || index === questions.length - 1 ? (
              <Button
                type="button"
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs shadow-sm flex items-center gap-1.5"
                onClick={() => setShowReviewModal(true)}
                disabled={submitting}
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                <span>Review & Finish</span>
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold text-xs shadow-sm flex items-center gap-1"
                onClick={() => setIndex((current) => current + 1)}
                disabled={submitting}
              >
                <span>Next</span>
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
        </footer>
      )}

      {/* 4. Pre-Submission Safety Review Confirmation Dialog */}
      {showReviewModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-xs transition-opacity"
          role="dialog"
          aria-modal="true"
          aria-labelledby="review-dialog-title"
          onClick={() => setShowReviewModal(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header: Title & Close Button */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="review-dialog-title" className="text-lg font-bold text-slate-900 tracking-tight">
                  Review & Submit Assessment
                </h2>
                <p className="mt-1 text-xs font-medium text-slate-600">
                  {title || "Diagnostic Assessment"}
                </p>
                <p className="mt-0.5 font-mono text-xs text-purple-700">
                  attempt: {attemptId.slice(0, 13)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowReviewModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors shrink-0"
                aria-label="Close review dialog"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            {/* Telemetry Metrics Panel (Clean 3-column divided card) */}
            <div className="my-5 grid grid-cols-3 divide-x divide-slate-200 rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 text-center shadow-2xs">
              <div className="px-2">
                <div className="font-mono text-2xl font-bold text-purple-700">{answeredCount}</div>
                <div className="mt-0.5 text-xs font-semibold text-slate-600">Answered</div>
              </div>
              <div className="px-2">
                <div className="font-mono text-2xl font-bold text-amber-600">{flagged.size}</div>
                <div className="mt-0.5 text-xs font-semibold text-slate-600">Flagged</div>
              </div>
              <div className="px-2">
                <div className="font-mono text-2xl font-bold text-slate-700">{questions.length - answeredCount}</div>
                <div className="mt-0.5 text-xs font-semibold text-slate-600">Unanswered</div>
              </div>
            </div>

            {/* Status Feedback Note */}
            {questions.length - answeredCount > 0 ? (
              <div className="mb-6 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-900">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                <div className="leading-relaxed">
                  <span className="font-bold">You have {questions.length - answeredCount} unanswered question{questions.length - answeredCount > 1 ? "s" : ""}.</span> You can return to complete them while time remains, or submit now for scoring.
                </div>
              </div>
            ) : (
              <div className="mb-6 flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-900">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                <span className="font-medium">All {questions.length} questions answered. Ready for evaluation.</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowReviewModal(false)}
                className="text-xs font-semibold text-slate-600 hover:text-slate-900"
              >
                Return to Assessment
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setShowReviewModal(false);
                  void finish();
                }}
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-4 py-2 shadow-sm"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                <span>Confirm & Submit</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
